import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { ActionBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { IapScope, ORGANIZATION } from '../ICSConstants';

export type OrgActionBase = ActionBase & {
  id: UUID;
  actionId: UUID;
  poc: string | null;
  briefedPoc: boolean;
  startDate: Date | null;
  status: string | null;
  targetDate: Date | null;
  actualDate: Date | null;
  utcOffset?: number;
  parentId?: UUID | null;
  eventId?: UUID | null;
  severity?: string | null;
  notificationTemplateId?: UUID | null;
};

export type GetOrgActionResponse = OrgActionBase & {
  groupGuid: UUID;
  userGuid: UUID;
  userName?: string;
  pocUserName?: string;
  version: number;
  timestamp: Date;
  index?: number;
};

export type CreateOrgActionReqData = ActionBase & {
  userGuid?: UUID;
  poc?: string | null;
  briefedPoc?: boolean;
  startDate?: Date | string | null;
  status?: string | null;
  targetDate?: Date | string | null;
  actualDate?: Date | string | null;
  parentId?: UUID | null;
  utcOffset?: number;
  eventId?: UUID | null;
  severity?: string | null;
  notificationTemplateId?: UUID | null;
};

export type EditOrgActionRequest = CreateOrgActionReqData & {
  actionId: UUID;
  archived?: boolean;
};

export const keys = {
  base: ['org_actions'] as const,
  byGroupAndUser: (groupGuid: string, userId: string) =>
    [...keys.base, groupGuid, userId] as const,
} as const;

export const useOrgActions = (scope: IapScope = ORGANIZATION) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.byGroupAndUser(groupGuid, userGuid),
    queryFn: async () => {
      const resp = await axios.get<GetOrgActionResponse[]>(
        `${config.apiGateway.ics_org_actions}/${groupGuid}`
      );
      return resp.data.map((a) => ({
        ...a,
        startDate: toUtcIso(a.startDate) as GetOrgActionResponse['startDate'],
        targetDate: toUtcIso(
          a.targetDate
        ) as GetOrgActionResponse['targetDate'],
        actualDate: toUtcIso(
          a.actualDate
        ) as GetOrgActionResponse['actualDate'],
        timestamp: toUtcIso(a.timestamp) as GetOrgActionResponse['timestamp'],
      }));
    },
    enabled: scope === ORGANIZATION && !!groupGuid,
  });
};

export const useCreateOrgAction = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOrgActionReqData) => {
      return await axios.post<GetOrgActionResponse>(
        `${config.apiGateway.ics_org_actions}/${groupGuid}`,
        { ...data, userGuid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(groupGuid, userGuid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateOrgAction = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditOrgActionRequest) => {
      return await axios.put(
        `${config.apiGateway.ics_org_actions}/${groupGuid}/${data.actionId}`,
        { ...data, userGuid, archived: data.archived ?? false }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.byGroupAndUser(groupGuid, userGuid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
